// 컴포저 — 바닐라 #composer 이식: textarea 자동 높이, Enter 전송(Shift+Enter 줄바꿈,
// 한글 IME 조합 가드), 전송/중지 버튼, 빈 상태 placeholder 롤링,
// 파일 첨부(칩·다중·제거) + 빠른 실행(⚡) 팝오버 (Phase 3-B).
// pendingFiles 는 ChatPage 소유 — 히어로↔도크 전환에도 첨부 상태가 유지된다.
import { useEffect, useRef, useState } from "react";
import { PLACEHOLDER_POOL, shuffled } from "../../lib/suggestions";
import { Chip } from "../../ui/Chip";
import { Button } from "../../ui/Button";

interface Props {
  busy: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  /** 빈 상태(히어로 슬롯)에서 placeholder 롤링 */
  hero?: boolean;
  files: File[];
  onFilesChange: (files: File[]) => void;
  /** 빠른 실행 패널 내용 — 열 때마다 렌더 (조회 중 기업의 연관 질문 반영) */
  getQuickGroups: () => Array<[string, string[]]>;
  /** 칩에 1초 이상 머무르면 그 질문이 회색 고스트로 예고된다 (웰컴 카드 → ChatPage 가 전달) */
  hoverSuggestion?: string;
  /** 고스트를 입력창에 채워 소비했을 때 — 부모가 hover 상태를 비운다 */
  onConsumeHover?: () => void;
  /** 이 컴포저 안(빠른 실행 패널)의 칩 호버를 부모에 알린다 */
  onHoverChip?: (text: string) => void;
}

const PH_IDLE = "궁금한 내용을 입력해주세요";

const ACCEPT = ".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.png,.jpg,.jpeg,.webp";

export default function Composer({
  busy,
  onSend,
  onStop,
  hero,
  files,
  onFilesChange,
  getQuickGroups,
  hoverSuggestion,
  onConsumeHover,
  onHoverChip,
}: Props) {
  const [value, setValue] = useState("");
  // 예시 질문(placeholder) — 비어 있을 때 회색으로 떠 있고, Tab·전송 버튼으로 바로 조회.
  // 키보드로 직접 입력하면 사라진다(dismissed).
  const [suggestion, setSuggestion] = useState("");
  const [focused, setFocused] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickGroups, setQuickGroups] = useState<Array<[string, string[]]>>([]);
  const suggestionRef = useRef("");
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // 예시 질문 롤링 (3.5초) — 기업 조회·발굴 칩에서 무작위 순서로.
  // 입력창에 포커스가 가면 그 질문에서 멈춘다(그대로 Tab 으로 조회 가능).
  useEffect(() => {
    if (!hero) {
      setSuggestion("");
      return;
    }
    const order = shuffled(PLACEHOLDER_POOL);
    let idx = 0;
    setSuggestion(order[0] || "");
    if (focused) return; // 포커스 중에는 고정 — 롤링 타이머를 걸지 않는다
    const timer = setInterval(() => {
      idx = (idx + 1) % order.length;
      setSuggestion(order[idx]);
    }, 3500);
    return () => clearInterval(timer);
    // focused 가 바뀌면 정리 후 재실행 — 포커스 시 타이머만 멈추고 문구는 유지된다
    // (아래 freezeRef 로 현재 문구를 이어받는다)
  }, [hero, focused]);

  // 빠른 실행 패널 — 밖 클릭 시 닫힘 (바닐라 document 위임)
  useEffect(() => {
    if (!quickOpen) return;
    const close = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (formRef.current && formRef.current.contains(t)) return;
      setQuickOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [quickOpen]);

  // 포커스로 effect 가 재실행돼도 보고 있던 문구가 유지되도록 되돌려 준다
  useEffect(() => {
    if (suggestion) suggestionRef.current = suggestion;
  }, [suggestion]);
  useEffect(() => {
    if (focused && suggestionRef.current) setSuggestion(suggestionRef.current);
  }, [focused]);

  // 칩 호버로 예고된 질문이 있으면 롤링 예시보다 우선한다 (사용자 의도가 명시적)
  const ghost = hoverSuggestion || suggestion;
  /** 지금 Tab·전송·클릭으로 바로 쓸 수 있는 예시 질문 (없으면 "") */
  const armed = !busy && !value.trim() ? ghost : "";

  /** 고스트를 입력값으로 확정 — 칩 호버 예고를 클릭 한 번으로 편집 가능하게 */
  const fillGhost = () => {
    if (!armed) return;
    setValue(armed);
    if (hoverSuggestion) onConsumeHover?.();
    requestAnimationFrame(autoHeight);
  };

  /** 칩 호버 1초 후 예고 — 스쳐 지나갈 때는 뜨지 않게 */
  const hoverTimer = useRef<number | undefined>(undefined);
  const armHover = (q: string) => {
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => onHoverChip?.(q), 1000);
  };
  const cancelHover = () => window.clearTimeout(hoverTimer.current);

  const autoHeight = () => {
    const ta = taRef.current;
    if (!ta || !ta.offsetParent) return; // 숨김/이동 중 잘못된 측정 방지
    ta.style.height = "auto";
    ta.style.height = `${Math.min(Math.max(ta.scrollHeight, 24), 128)}px`;
  };

  // 중지 버튼이 전송 버튼으로 교체된 직후의 클릭이 '빈 입력 + 예고 전송'으로
  // 새어 같은 질문이 다시 실행되던 문제 방지 — 짧은 냉각 시간을 둔다.
  const busyEndedAt = useRef(0);
  const prevBusy = useRef(busy);
  useEffect(() => {
    if (prevBusy.current && !busy) busyEndedAt.current = Date.now();
    prevBusy.current = busy;
  }, [busy]);

  const submit = () => {
    const text = value.trim();
    // 생성 중엔 글자 입력만 대기열로 — 빈 전송·예고 질문은 통과시키지 않는다
    if (busy && !text) return;
    // 빈 입력 전송(=예고 질문 조회)은 중지 직후 600ms 동안 무시
    if (!text && !files.length && Date.now() - busyEndedAt.current < 600) return;
    // 입력이 비어 있고 예시 질문이 떠 있으면 그 질문을 그대로 조회한다
    const send = text || (files.length ? "(첨부 파일 참고)" : armed);
    if (!send) return;
    onSend(send);
    setValue("");
    const ta = taRef.current;
    if (ta) ta.style.height = "auto";
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Tab — 예시 질문을 입력창에 채운다(자동완성). 조회는 Enter/전송 버튼으로.
    if (e.key === "Tab" && armed && !e.shiftKey) {
      e.preventDefault();
      fillGhost();
      return;
    }
    if (e.nativeEvent.isComposing || e.keyCode === 229) return; // 한글 IME 조합 중 전송 안 함
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const pickQuick = (q: string) => {
    setQuickOpen(false);
    if (busy) {
      // 생성 중엔 입력창에 담아둔다 (바닐라)
      setValue(q);
      taRef.current?.focus();
      return;
    }
    onSend(q);
  };

  return (
    <form
      ref={formRef}
      className={`composer${value.trim() || armed ? " has-text" : ""}${busy ? " busy" : ""}`}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {/* 빠른 실행 팝오버 — 내용은 열 때마다 렌더 (현재 조회 중 기업 반영) */}
      <div className="quick-panel" hidden={!quickOpen}>
        {quickGroups.map(([title, items]) => (
          <div className="quick-col" key={title}>
            <div className="quick-title">{title}</div>
            {items.map((q) => (
              <Chip
                variant="quick"
                key={q}
                onMouseEnter={() => armHover(q)}
                onMouseLeave={cancelHover}
                onClick={(e) => {
                  e.stopPropagation();
                  cancelHover();
                  pickQuick(q);
                }}
              >
                {q}
              </Chip>
            ))}
          </div>
        ))}
      </div>
      <div className="file-chips">
        {files.map((f, i) => (
          <span className="file-chip" key={`${f.name}-${i}`}>
            📎 {f.name}{" "}
            <button
              type="button"
              aria-label={`${f.name} 첨부 제거`}
              onClick={() => onFilesChange(files.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          </span>
        ))}
      </div>
      {/* 예시 질문 고스트 — placeholder 속성은 포커스 시 지우는 브라우저가 있어
          실제 요소로 겹쳐 그린다 (클릭해도 회색 유지, Tab/전송으로 조회) */}
      {hero && armed && (
        <span className="ph-ghost" aria-hidden="true">
          {armed}
          <kbd className="ph-tab">Tab</kbd>
        </span>
      )}
      <textarea
        ref={taRef}
        rows={1}
        placeholder={hero && armed ? "" : PH_IDLE}
        value={value}
        onChange={(e) => {
          // 직접 입력이 시작되면 예시 질문은 가려진다 (다 지우면 다시 보임 — 일반 placeholder 규칙)
          setValue(e.target.value);
          autoHeight();
        }}
        // 클릭해도 예고는 회색 그대로 — Tab 으로 채워 수정하거나,
        // 그 상태에서 전송(조회) 버튼을 누르면 그 문장으로 바로 검색된다.
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={handleKey}
      />
      <div className="composer-row">
        <Button
          variant="attach"
          title="파일 첨부"
          aria-label="파일 첨부"
          onClick={() => fileRef.current?.click()}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
        </Button>
        <Button
          variant="ghost"
          active={quickOpen}
          title="빠른 실행"
          aria-label="빠른 실행"
          onClick={(e) => {
            e.stopPropagation();
            if (!quickOpen) setQuickGroups(getQuickGroups());
            setQuickOpen(!quickOpen);
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
          </svg>
        </Button>
        <span className="spacer" />
        {busy ? (
          <Button variant="stop" aria-label="중단" title="응답 중단" onClick={onStop}>
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          </Button>
        ) : (
          <Button variant="send" type="submit" aria-label="전송" title="전송">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 19V5" />
              <path d="m5 12 7-7 7 7" />
            </svg>
          </Button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        multiple
        style={{ display: "none" }}
        accept={ACCEPT}
        onChange={(e) => {
          onFilesChange([...files, ...Array.from(e.target.files || [])]);
          e.target.value = ""; // 같은 파일 재선택 허용
        }}
      />
    </form>
  );
}
